/*
 * Copyright (c) CIRROM 2026.
 */

import {
  acceptSuggestedChangesWithModifications,
  approveMultipleChange,
  approveSuggestedChange,
  transitionSuggestedChange,
} from "../../../api/suggestedchanges";
import {
  acceptCirromMonitorChangeWithCompany,
  acceptCirromMonitorSuggestedChange,
  rejectCirromMonitorSuggestedChanges,
} from "../../../api/cirrommonitor";
import { CandidateValueGroup, FieldCandidate } from "./normalize";

/*
 * Write actions for the unified ledger. The two change pipelines keep their
 * own endpoints; this module is the routing layer that lets a ledger row act
 * on a candidate without caring which system proposed it.
 */

export interface ActionContext {
  recordId: number;
  integratedSystemId: number;
}

/**
 * The candidate a group action applies. Cirrom first: its approval runs
 * through the suggested-change pipeline the steward's queue tracks, and on an
 * agreement row the monitor twin disappears on refetch once the CRM value
 * matches it.
 */
export const preferredCandidate = (
  group: CandidateValueGroup
): FieldCandidate =>
  group.candidates.find((candidate) => candidate.source === "cirrom") ??
  group.candidates[0];

export const approveCandidate = async (
  candidate: FieldCandidate,
  context: ActionContext
): Promise<any> => {
  if (candidate.source === "monitor") {
    return acceptCirromMonitorSuggestedChange([
      {
        record_id: context.recordId,
        integrated_system_id: context.integratedSystemId,
        suggestion_id: candidate.sourceIds.monitorId,
      },
    ]);
  }
  if (candidate.sourceIds.choiceId) {
    return approveMultipleChange(
      candidate.sourceIds.suggestedChangeId,
      candidate.sourceIds.choiceId
    );
  }
  return approveSuggestedChange(candidate.sourceIds.suggestedChangeId);
};

/** An account as the picker hands it over: display name plus the CRM's id. */
export interface CrmAccount {
  name: string;
  crm_account_id: string;
}

/**
 * Linking the record to an account outright, with no change behind it.
 *
 * This is the ad-hoc company edit: the steward decided the employer is wrong
 * (or missing) without waiting for anything to propose it. The endpoint is
 * record-scoped rather than change-scoped, which is exactly why it can serve
 * both this and an accepted Monitor detection - there is one way to set a
 * company, and it takes an account.
 */
export const setCompanyAccount = async (
  account: CrmAccount,
  context: ActionContext
): Promise<any> =>
  acceptCirromMonitorChangeWithCompany(
    context.recordId,
    account.name,
    account.crm_account_id,
    context.integratedSystemId
  );

/**
 * Approving a COMPANY, which is never a string write.
 *
 * A company name on its own is a label; what the CRM stores is a link to an
 * account record, and writing the detected text would leave the contact
 * unlinked while looking correct on screen. So both pipelines take the account
 * the steward picked rather than the value the source proposed - the proposed
 * name is only the hint that starts the search.
 *
 * Each pipeline has its own endpoint for it: Monitor accepts the detection
 * against the account, and a Cirrom change is accepted "with modifications",
 * which is how that pipeline carries a chosen account id.
 */
export const approveCompanyWithAccount = async (
  candidate: FieldCandidate,
  account: CrmAccount,
  context: ActionContext
): Promise<any> => {
  if (candidate.source === "monitor") {
    return setCompanyAccount(account, context);
  }
  return acceptSuggestedChangesWithModifications(
    candidate.sourceIds.suggestedChangeId,
    account.name,
    account.crm_account_id
  );
};

/**
 * Rejecting a value group rejects every candidate behind it, in both
 * pipelines - rejecting only one twin of an agreement row would make the
 * other reappear on the next refetch. Rejecting one value of a contested row
 * deliberately leaves the other values pending: that cross-value semantic is
 * still an open product decision.
 */
export const rejectGroup = async (
  group: CandidateValueGroup
): Promise<void> => {
  const cirromIds = Array.from(
    new Set(
      group.candidates
        .filter((candidate) => candidate.source === "cirrom")
        .map((candidate) => candidate.sourceIds.suggestedChangeId)
    )
  ).filter(Boolean) as number[];
  const monitorIds = Array.from(
    new Set(
      group.candidates
        .filter((candidate) => candidate.source === "monitor")
        .map((candidate) => candidate.sourceIds.monitorId)
    )
  ).filter(Boolean) as number[];

  const calls: Promise<any>[] = [];
  if (cirromIds.length) {
    calls.push(transitionSuggestedChange(cirromIds, true));
  }
  if (monitorIds.length) {
    calls.push(rejectCirromMonitorSuggestedChanges(monitorIds));
  }

  // allSettled so one pipeline refusing cannot hide the other's outcome;
  // any refusal surfaces as an error and the refetch shows what is left.
  const results = await Promise.allSettled(calls);
  if (results.some((result) => result.status === "rejected")) {
    throw new Error("Rejection failed");
  }
};
